/**
 * Where a session is browsing from, for segmentation (HB-957).
 *
 * Instagram (780k events) and Facebook (779k) in-app browsers together exceed
 * desktop Chrome (1.48M). That traffic is real but shallow (~1.8 pages/session),
 * so it must be *segmented* rather than filtered — on-site engagement can only
 * be judged fairly once in-app sessions are separated from standard browsers.
 */
export type TrafficSegment = 'instagram' | 'facebook' | 'tiktok' | 'twitter' | 'browser';

/**
 * In-app browser UA signatures.
 *
 * Order matters: Instagram's in-app UA also carries the `FBAV`/`FB_IAB` Facebook
 * tokens, so Instagram must be tested first or its sessions land in `facebook`.
 */
const IN_APP_BROWSER_SIGNATURES: ReadonlyArray<{
	segment: TrafficSegment;
	pattern: RegExp;
}> = [
	{ segment: 'instagram', pattern: /instagram/i },
	{ segment: 'facebook', pattern: /FBAN|FBAV|FB_IAB|FBIOS/i },
	{ segment: 'tiktok', pattern: /musical_ly|bytedance|tiktok/i },
	{ segment: 'twitter', pattern: /twitter/i }
];

/**
 * The traffic segment for the current client, defaulting to `browser`.
 *
 * Server-side this returns `browser` — SSR has no navigator, and the value is
 * only ever used to label client-emitted events.
 */
export const getTrafficSegment = (): TrafficSegment => {
	if (typeof navigator === 'undefined') {
		return 'browser';
	}

	const userAgent = navigator.userAgent;

	if (!userAgent) {
		return 'browser';
	}

	return (
		IN_APP_BROWSER_SIGNATURES.find(({ pattern }) => pattern.test(userAgent))?.segment ?? 'browser'
	);
};

/** True when the session is inside a social app's embedded browser. */
export const isInAppBrowser = (): boolean => getTrafficSegment() !== 'browser';
